import { useState } from 'react';
import { API_URL } from '@constants';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { useTranslation } from 'react-i18next';

const buildEndpoint = (uuid: string) => `/draft-registration/validate-account/${uuid}`;

export type RegistrationPasswordStepInput =
    paths['/api/v1/draft-registration/validate-account/{uuid}']['post']['requestBody']['content']['multipart/form-data'];

export type RegistrationPasswordStepSuccessResponse =
    paths['/api/v1/draft-registration/validate-account/{uuid}']['post']['responses']['200']['content']['application/json'];

type ValidationErrors = Record<string, string[]>;

export const useOtpStep = () => {
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { draft, setOtpStep } = useData();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors | null>(null);

    const request = async (props: RegistrationPasswordStepInput) => {
        setLoading(true);
        setSuccess(null);
        setError(null);
        setErrorMessage(null);
        setValidationErrors(null);

        try {
            const uuid = draft?.uuid ?? '';
            if (uuid.length == 0) {
                const error = new Error() as ApiError;
                error.message = t('otp.userId_missing');
                throw error;
            }

            const response = await fetch(`${API_URL}${buildEndpoint(uuid)}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...props }),
            });

            if (!response.ok) {
                const error = new Error() as ApiError;

                try {
                    const json = await response.json();
                    error.message = json.message;
                    error.errors = json.errors;
                } catch (error: any) {
                    error.message = '';
                }

                error.statusCode = response.status;
                throw error;
            }

            const data = await response.json();
            setSuccess(data.message);
            setOtpStep?.({
                verified: true,
                timestamp: Date.now(),
                uuid: draft?.uuid ?? null,
            });

        } catch (error: any) {
            handleCommonErrors(error);
            error.message && setError(error.message);
            error.errors && setValidationErrors(error.errors);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    const resendOtp = async () => {
        const email = draft?.email;
        if (!email) {
            setErrorMessage(t('otp.emailError'));
            return;
        }

        setLoading(true);
        setSuccess(null);
        setErrorMessage(null);

        try {
            const response = await fetch(`${API_URL}/draft-registration/resend-otp`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), email  }),
            });

            if (!response.ok) throw new Error(t('otp.failedToResent'));

            const data = await response.json();
            setSuccess(data.message);

            setOtpStep?.({
                verified: true,
                timestamp: Date.now(),
                uuid: draft?.uuid ?? null,
            });

        } catch (error) {
            const e = error as ApiError;
            handleCommonErrors(e);
            if (e.message) setErrorMessage(e.message);
            throw e;
        } finally {
            setLoading(false);
        }
    };

    return {
        request,
        resendOtp,
        loading,
        success,
        error,
        errorMessage,
        validationErrors,
        lastResponseTime,
    };
};
